import { useQuery, useQueryClient } from '@tanstack/react-query';
import { debounce } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  DashboardV2ViewState,
  DEFAULT_VIEW_STATE,
  decodeViewState,
  encodeViewState,
  hasCustomerSelection,
  savedFiltersClient,
  stripCustomerLock,
} from '../api/savedFilters';
import {
  DashboardFilterValues,
  QueueFilterValues,
  QueueName,
} from '../api/types';

import useAuth from '@/hooks/useAuth';

// Owns every filter the admin chose on Dashboard V2, and keeps it (GRAVY-10856).
//
// The page used to hold this in plain `useState`, so it survived a tab switch
// and nothing else — opening a shift and pressing Back, refreshing, or a new
// session all dropped it. Dashboard V1 saves its filters in three places, and
// this is the same three for V2:
//
//   React state  — the session, the source the page renders from
//   the URL      — Back, refresh and shared links
//   the server   — logout/login, and a different machine
//
// ## Hydration order, and why it is URL first
//
// A URL is an explicit request for a particular view — the admin pressed Back
// to it, or someone sent them the link. The saved filter is only a default. So
// the URL wins when it has an opinion, and the server is read only when it does
// not. That is V1's order, and reversing it would make a shared link silently
// show the recipient their own filters instead.
//
// ## Why the query cache is written as well as read
//
// V1 also holds its filters in Redux, which outlives the page: leaving through
// the sidebar and coming back finds them still there, with no URL param and no
// request. V2's equivalent is the app's shared QueryClient — the saved filter
// is cached under one key, so a later mount reads what this session already
// knows instead of re-fetching.
//
// That only works if the cache is kept honest. With `staleTime: Infinity` a
// mount that answered "nothing saved" pins that answer for the life of the
// cache entry, so every filter the admin then chose was invisible to the next
// mount and the dashboard came back empty (GRAVY-10856 QA). Every write to the
// server therefore writes the same value into the cache.
//
// Refetching instead — `gcTime: 0` — would be both slower and wrong: the PUT
// from the unmounting page and the GET from the mounting one race, and the GET
// can win and restore the value the PUT is in the middle of replacing.
//
// ## Why the URL is written with history.replaceState
//
// The week (`?sd=`) and the Week/Day span (`?view=`) are already written that
// way by `useCalendarNav`. Mixing mechanisms is what breaks a query string:
// react-router builds `setSearchParams` from ITS OWN last-known location, so a
// router write after a raw write drops the raw params. Writing filters the same
// way `sd` and `view` are written keeps all of them intact.
//
// `replaceState` rather than `pushState` is deliberate: choosing a filter is not
// a navigation step, and Back should leave the dashboard rather than walk the
// admin back through every pill they tried.

const URL_PARAM = 'f';

// Saved filters are deliberately NOT under the `dashboard_v2` query root. That
// root is invalidated on every tab change so badges and lists revalidate
// together; a stored preference has nothing to do with that, and refetching it
// on each tab switch would be pure noise.
const SAVED_FILTERS_KEY = ['dashboard_v2_saved_filters'] as const;

/** Matches V1's `userFilterSave` debounce — a dragged selection saves once. */
const SAVE_DEBOUNCE_MS = 600;

const readUrlState = (): DashboardV2ViewState | null => {
  try {
    const raw = new URLSearchParams(window.location.search).get(URL_PARAM);
    if (!raw) return null;

    // `URLSearchParams.get` has already percent-decoded once, which is all a
    // link written by `writeUrlState` needs. A link written by the build that
    // encoded the value a second time arrives still encoded, so it is decoded
    // again rather than dropped — those links are already in circulation.
    return (
      decodeViewState(raw) ??
      (raw.includes('%') ? decodeViewState(decodeURIComponent(raw)) : null)
    );
  } catch {
    // A malformed param must not stop the dashboard from loading — fall
    // through to the saved filter, then to no filter at all.
    return null;
  }
};

const writeUrlState = (encoded: string | null) => {
  const params = new URLSearchParams(window.location.search);
  // Not `encodeURIComponent`: `toString` percent-encodes the value on the way
  // out, so pre-encoding it produced `%257B…` — readable, but twice the length
  // in every link an admin shares.
  if (encoded) params.set(URL_PARAM, encoded);
  // Dropped rather than left empty, so a reset produces the same clean URL the
  // admin would get from the sidebar link.
  else params.delete(URL_PARAM);

  const query = params.toString();
  window.history.replaceState(
    {},
    '',
    query ? `${window.location.pathname}?${query}` : window.location.pathname,
  );
};

const useDashboardV2ViewState = () => {
  const { currentAdmin, isCurrentCustomerAdmin } = useAuth();
  // Guarded for the same reason CalendarFilterBar guards it: `currentAdmin` is
  // null while the session resolves, and `isCurrentCustomerAdmin` reads
  // `.customer` off its argument.
  const customerAdmin = !!currentAdmin && isCurrentCustomerAdmin(currentAdmin);

  const queryClient = useQueryClient();

  // Read ONCE, synchronously, before the first paint — so a restored filter is
  // part of the first calendar_week request rather than a second one that
  // replaces the unfiltered result the admin already saw. Both synchronous
  // sources are consulted here, in the order above.
  const [initial] = useState(() => {
    const fromUrl = readUrlState();
    // The URL is authoritative and says nothing about what the server holds,
    // so `lastSaved` stays unknown: the first write then pushes this view to
    // the server, which is how following a shared link adopts it (as in V1).
    if (fromUrl)
      return { state: fromUrl, hydrated: true, lastSaved: undefined };

    const cached = queryClient.getQueryData<string | null>(SAVED_FILTERS_KEY);
    // `undefined` is the only value that means "not cached" — `null` is a
    // cached answer, and a real one: this admin has nothing saved.
    if (cached === undefined) {
      return { state: null, hydrated: false, lastSaved: undefined };
    }

    const restored = decodeViewState(cached);
    return {
      state: restored,
      hydrated: true,
      // Normalized through the encoder rather than kept as-is, so a blob from
      // an older build does not read as a change and re-save on every mount.
      lastSaved: restored ? encodeViewState(restored) : null,
    };
  });

  const [state, setState] = useState<DashboardV2ViewState>(
    () => initial.state ?? DEFAULT_VIEW_STATE,
  );

  // Only asked for when neither synchronous source had anything to say. With a
  // filter already in hand the answer cannot change anything, so no request.
  const saved = useQuery({
    queryKey: SAVED_FILTERS_KEY,
    queryFn: () => savedFiltersClient.get(),
    enabled: !initial.hydrated,
    staleTime: Infinity,
    // One attempt. A saved filter is a convenience; retrying would only delay
    // the moment the dashboard is usable.
    retry: false,
    refetchOnWindowFocus: false,
  });

  // Nothing may be written back before the restore has had its turn — otherwise
  // the empty state of the first render overwrites the very filter being
  // fetched. True already when a synchronous source decided, because then there
  // is nothing to wait for.
  const hydrated = useRef(initial.hydrated);
  // What the server is known to hold, so an unchanged state does not re-save on
  // every render. `undefined` means "not known yet".
  const lastSaved = useRef<string | null | undefined>(initial.lastSaved);

  useEffect(() => {
    if (hydrated.current) return;
    // `isPending` stays true forever while the query is disabled, but that path
    // is already hydrated above, so reaching here means it really is in flight.
    if (saved.isPending) return;

    // On failure, fall through to the default state rather than blocking: the
    // dashboard must load whether or not a preference could be read.
    const restored = saved.isSuccess ? decodeViewState(saved.data) : null;
    if (restored) {
      setState(customerAdmin ? stripCustomerLock(restored) : restored);
    }
    lastSaved.current = restored ? encodeViewState(restored) : null;
    hydrated.current = true;
  }, [saved.isPending, saved.isSuccess, saved.data, customerAdmin]);

  // The role can resolve AFTER hydration — `currentAdmin` is null on the first
  // renders — so the lock is re-applied when it lands rather than only at
  // restore time. Without this, a Client Admin whose session resolved late
  // would keep a Client selection out of a shared link.
  useEffect(() => {
    if (!customerAdmin) return;
    setState((prev) =>
      hasCustomerSelection(prev) ? stripCustomerLock(prev) : prev,
    );
  }, [customerAdmin]);

  const persistToServer = useMemo(
    () =>
      debounce((encoded: string | null) => {
        if (encoded === lastSaved.current) return;
        lastSaved.current = encoded;

        // In step with the server write, not after it. This is the session's
        // own answer to "what would be restored now", the job V1 gives Redux —
        // so leaving through the sidebar and coming straight back shows what
        // the admin was looking at, whether or not the PUT has landed yet.
        queryClient.setQueryData(SAVED_FILTERS_KEY, encoded);

        // Nothing selected means nothing to remember: the row is deleted rather
        // than left holding an empty filter, so "Reset filters" survives a
        // reload instead of being undone by the next hydration.
        const request = encoded
          ? savedFiltersClient.save(encoded)
          : savedFiltersClient.clear();

        request.catch(() => {
          // Swallowed on purpose. React state and the URL are authoritative for
          // this session, and a failed preference write is not something to put
          // in front of an admin working a queue. Allow the next change to try
          // again rather than pretending it landed.
          lastSaved.current = undefined;
        });
      }, SAVE_DEBOUNCE_MS),
    // `queryClient` is the app-wide instance and never changes identity, so the
    // debounce is still created once and its pending timer is never orphaned.
    [queryClient],
  );

  // A change made just before leaving the page would otherwise die with the
  // debounce timer — which is precisely the "opened a shift straight after
  // filtering" case this ticket is about.
  useEffect(() => () => persistToServer.flush(), [persistToServer]);

  useEffect(() => {
    if (!hydrated.current) return;

    const encoded = encodeViewState(
      customerAdmin ? stripCustomerLock(state) : state,
    );
    writeUrlState(encoded);
    persistToServer(encoded);
  }, [state, customerAdmin, persistToServer]);

  // ── Setters ─────────────────────────────────────────────────────────────
  //
  // Stable identities: the queues correct their own page from an effect when
  // live results shrink (`useQueuePageClamp`), and an unstable callback in that
  // effect's dependencies would re-run it on every render.

  const setCalendarFilters = useCallback((next: DashboardFilterValues) => {
    setState((prev) => ({ ...prev, calendar: next }));
  }, []);

  const setCalendarFiltersOpen = useCallback((next: boolean) => {
    setState((prev) => ({ ...prev, calendarFiltersOpen: next }));
  }, []);

  const setQueueFilters = useCallback(
    (queue: QueueName, next: QueueFilterValues) => {
      setState((prev) => ({
        ...prev,
        queues: { ...prev.queues, [queue]: next },
        // Changing a filter resets that queue to page 1. Staying on page 4 of a
        // narrower result set would land the admin on an empty page and read as
        // "the filter returned nothing".
        queuePages: { ...prev.queuePages, [queue]: 1 },
      }));
    },
    [],
  );

  const setQueuePage = useCallback((queue: QueueName, next: number) => {
    setState((prev) => ({
      ...prev,
      queuePages: { ...prev.queuePages, [queue]: next },
    }));
  }, []);

  return {
    calendarFilters: state.calendar,
    calendarFiltersOpen: state.calendarFiltersOpen,
    queueFilters: state.queues,
    queuePages: state.queuePages,
    setCalendarFilters,
    setCalendarFiltersOpen,
    setQueueFilters,
    setQueuePage,
  };
};

export default useDashboardV2ViewState;
